/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

/* tslint:disable: no-duplicate-imports */
import * as Msdyn365 from '@msdyn365-commerce/core';
import { generateProductImageUrl } from '@msdyn365-commerce-modules/retail-actions';
import {
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext
} from '@msdyn365-commerce/core';
import { ProductSearchCriteria, ProductSearchResult } from '@msdyn365-commerce/retail-proxy';
import { searchByCriteriaAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import {
    BaseCollectionInput,
    createBaseCollectionInput
} from '../../mfrm-search-result-container/actions/base-collection-action';
import { IFullProductsSearchResultsWithCount } from './IFullProductsSearchResultsWithCount';

/**
 * GetFullProductsByCollection Action Input
 */
export class GetFullProductsBySearchInput extends BaseCollectionInput implements IActionInput {
    public getCacheObjectType = () => 'FullProductSearchResultBySearch';
    public dataCacheType = (): Msdyn365.CacheType => 'application';
}

/**
 * createInput function which creates and actionInput used to fetch products for a list page.
 */
const createInput = (args: ICreateActionContext<{ itemsPerPage: number; includedAttributes: boolean | undefined }>): IActionInput => {
    const input = createBaseCollectionInput(args, GetFullProductsBySearchInput);

    // Set Top
    if (input.queryResultSettings.Paging && args.config) {
        input.queryResultSettings.Paging.Top = args.config.itemsPerPage || 1;
    }

    // Set Skip
    if (input.queryResultSettings.Paging && args.requestContext.query && args.requestContext.query.skip) {
        input.queryResultSettings.Paging.Skip = +args.requestContext.query.skip;
    }

    input.queryResultSettings.count = true;

    return input;
};

/**
 * Action function to fetch products for a list page
 */
// tslint:disable-next-line: max-func-body-length cyclomatic-complexity
async function action(
    input: GetFullProductsBySearchInput,
    ctx: IActionContext
): Promise<IFullProductsSearchResultsWithCount> {
    // let promise: AsyncResult<ProductSearchResult[]>;
    // let searchProductId;
    const searchCriteriaInput: ProductSearchCriteria = {};
    searchCriteriaInput.Context = { ChannelId: ctx.requestContext.apiSettings.channelId, CatalogId: ctx.requestContext.apiSettings.catalogId };
    searchCriteriaInput.Refinement = [];
    searchCriteriaInput.IncludeAttributes = false;
    searchCriteriaInput.SkipVariantExpansion = true;

    searchCriteriaInput.SearchCondition = input.searchText;
    const promise = searchByCriteriaAsync(
        {
            callerContext: ctx,
            queryResultSettings: input.queryResultSettings
        },
        searchCriteriaInput
    );

    const productSearchResults : ProductSearchResult[] = await promise;

    const mappedProducts = productSearchResults.map(productSearchResult => {
        const newImageUrl = generateProductImageUrl(
            productSearchResult,
            ctx.requestContext.apiSettings
        );

        if (newImageUrl) {
            productSearchResult.PrimaryImageUrl = newImageUrl;
        }

        return (productSearchResult);
    });


    return {
        products: mappedProducts,
        count: promise.metadata.count || 0,
        // VSI Customization 7294 Start
        virtualRefiners: searchCriteriaInput.Refinement,
        minusVirtualRefiner: input.refiners
        // VSI Customization 7294 End
    };
}

export default createObservableDataAction({
    id: '@msdyn365-commerce-modules/search/get-full-products-by-search',
    action: <IAction<IFullProductsSearchResultsWithCount>>action,
    input: createInput
});
